import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { streamPrediction } from "@/features/prediction/api/predictionStream";
import {
  initialPredictionRunState,
  predictionRunReducer,
} from "@/features/prediction/model/reducer";
import type {
  PredictionPayload,
  PredictionRequest,
} from "@/features/prediction/model/types";
import type { PredictionFormValues } from "@/features/prediction/model/form";
import {
  PredictionContext,
  type PredictionContextValue,
} from "@/features/prediction/model/predictionContextCore";
import {
  getPredictionHistory,
  getLatestPrediction,
} from "@/features/prediction/api/predictionHistory";
import {
  PREDICTION_HISTORY_QUERY_KEY,
  type PredictionHistoryRecord,
} from "@/features/prediction/model/predictionHistory";
import { DRAFT_STORAGE_KEY } from "@/features/prediction/storage/routeRecovery";
import { privateStorage } from "@/lib/privateStorage";
import { useSessionLease } from "@/lib/api/useSessionLease";
import { ApiError } from "@/lib/api/client";
import { isDemoMode } from "@/lib/api/demoMode";

const LAST_PREDICT_STORAGE_KEY = "osim_last_predict";

function readDraft(): PredictionRequest | null {
  try {
    const raw = privateStorage.getItem(DRAFT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PredictionRequest) : null;
  } catch {
    return null;
  }
}

function persistLatestBridge(
  request: PredictionRequest,
  payload: PredictionPayload,
  createdAt: number,
): void {
  try {
    privateStorage.setItem(
      LAST_PREDICT_STORAGE_KEY,
      JSON.stringify({
        id: payload.session_id || `local-${createdAt}`,
        ts: createdAt,
        caption: request.creative.caption,
        budget: request.total_budget,
        platform_alloc: request.platform_alloc,
        use_llm: request.use_llm,
        n_souls: request.n_souls,
        language_feedback_integrity_version:
          payload.extras?.language_feedback_integrity_version === 1 ? 1 : undefined,
        kpis: payload.kpis || {},
        soul_quotes: payload.soul_quotes || [],
        lifecycle: payload.lifecycle || null,
      }),
    );
  } catch {
    // IndexedDB remains the source of truth if this compatibility bridge is too large.
  }
}

export function PredictionProvider({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [run, dispatch] = useReducer(
    predictionRunReducer,
    initialPredictionRunState,
  );
  const [draft, setDraftState] = useState<PredictionRequest | null>(readDraft);
  const [latestRecord, setLatestRecord] =
    useState<PredictionHistoryRecord | null>(null);
  const [activeHistoryId, setActiveHistoryId] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const streamRef = useRef<{ generation: number; historyId: string | null } | null>(null);
  const userSelectionRef = useRef(false);
  const owner = useSessionLease();

  useEffect(() => {
    if (!owner.isCurrent()) return;
    let active = true;
    void getLatestPrediction()
      .then((record) => {
        if (!owner.isCurrent() || !active || !record || userSelectionRef.current) return;
        setLatestRecord(record);
        if ((record.status || "completed") === "running") {
          setDraftState(record.request);
          dispatch({
            type: "restore",
            status: "running",
            request: record.request,
            review: record.review || null,
            startedAt: record.createdAt,
            currentStep: record.currentStep || null,
            error: null,
          });
          setActiveHistoryId(record.id);
        }
      })
      .catch(() => {
        // History is an enhancement; prediction remains available without it.
      });
    return () => {
      active = false;
      controllerRef.current?.abort();
    };
  }, [owner]);

  useEffect(() => {
    const interrupted = streamRef.current;
    if (!owner.isCurrent() || !interrupted || interrupted.generation === owner.generation) return;
    streamRef.current = null;
    if (interrupted.historyId) setActiveHistoryId(interrupted.historyId);
    else dispatch({ type: "fail", message: t("prediction.resumeFromHistory"), finishedAt: Date.now() });
  }, [owner, t]);

  useEffect(() => {
    if (!activeHistoryId) return;
    let active = true;
    let timer: number | undefined;
    const unavailable = () => {
      dispatch({ type: "fail", message: t("prediction.historyUnavailable"), finishedAt: Date.now() });
      setActiveHistoryId(null);
    };

    const refresh = async () => {
      if (!owner.isCurrent()) return;
      try {
        const record = await getPredictionHistory(activeHistoryId);
        if (!owner.isCurrent() || !active) return;
        if (!record) { unavailable(); return; }
        setLatestRecord(record);
        const status = record.status || "completed";
        if (status === "completed" && record.payload) {
          dispatch({
            type: "show",
            request: record.request,
            review: record.review || null,
            payload: record.payload,
          });
          persistLatestBridge(record.request, record.payload, Date.now());
          setActiveHistoryId(null);
          await queryClient.invalidateQueries({
            queryKey: PREDICTION_HISTORY_QUERY_KEY,
          });
          return;
        }
        if (status === "failed") {
          dispatch({
            type: "restore",
            status: "failed",
            request: record.request,
            review: record.review || null,
            startedAt: record.createdAt,
            currentStep: record.currentStep || null,
            error: record.errorMessage || t("prediction.failed"),
          });
          setActiveHistoryId(null);
          return;
        }
        dispatch({
          type: "restore",
          status: "running",
          request: record.request,
          review: record.review || null,
          startedAt: record.createdAt,
          currentStep: record.currentStep || null,
          error: null,
        });
      } catch (error) {
        if (active && owner.isCurrent() && error instanceof ApiError && error.status === 404) {
          unavailable(); return;
        }
        // Keep the persisted progress visible and retry on the next interval.
      }
      if (active && owner.isCurrent()) timer = window.setTimeout(refresh, 2_000);
    };

    timer = window.setTimeout(refresh, 500);
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [activeHistoryId, owner, queryClient, t]);

  const setDraft = useCallback((request: PredictionRequest | null) => {
    if (!owner.isCurrent()) return;
    userSelectionRef.current = true;
    setDraftState(request);
    try {
      if (request) {
        privateStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(request));
      } else {
        privateStorage.removeItem(DRAFT_STORAGE_KEY);
      }
    } catch {
      // Keep the current in-memory draft.
    }
  }, [owner]);

  const startPrediction = useCallback(
    async (request: PredictionRequest, review: PredictionFormValues) => {
      if (!owner.isCurrent()) return;
      controllerRef.current?.abort();
      setActiveHistoryId(null);
      const controller = new AbortController();
      controllerRef.current = controller;
      const startedAt = Date.now();
      setDraft(request);
      const stream: { generation: number; historyId: string | null } = { generation: owner.generation, historyId: null };
      streamRef.current = stream;
      const current = () => owner.isCurrent() && streamRef.current === stream;
      dispatch({ type: "submit", request, review, startedAt });

      const query = new URLSearchParams(window.location.search);
      const demo = isDemoMode();
      const demoSpeed = Number.parseFloat(query.get("demo_speed") || "4");
      let historyAnnounced = false;

      try {
        const payload = await streamPrediction(request, {
          signal: controller.signal,
          demo,
          demoSpeed,
          historyReview: review,
          outputLanguage: i18n.language.startsWith("zh") ? "zh" : "en",
          onHistoryId: id => { if (current()) stream.historyId = id; },
          onEvent: (event) => {
            if (!current()) return;
            dispatch({ type: "event", event });
            if (!demo && !historyAnnounced) {
              historyAnnounced = true;
              void queryClient.invalidateQueries({
                queryKey: PREDICTION_HISTORY_QUERY_KEY,
              });
            }
          },
        });
        if (!current()) return;
        streamRef.current = null;
        const finishedAt = Date.now();
        dispatch({ type: "complete", payload, finishedAt });
        const record: PredictionHistoryRecord = {
          id: payload.session_id || `local-${finishedAt}`,
          createdAt: finishedAt,
          elapsedMs: finishedAt - startedAt,
          status: "completed",
          currentStep: "report_generation",
          errorMessage: null,
          request,
          review,
          kpis: payload.kpis || {},
          payload,
        };
        setLatestRecord(record);
        persistLatestBridge(request, payload, finishedAt);
        await queryClient.invalidateQueries({
          queryKey: PREDICTION_HISTORY_QUERY_KEY,
        });
      } catch (error) {
        if (!current()) return;
        streamRef.current = null;
        if (error instanceof DOMException && error.name === "AbortError") {
          dispatch({ type: "cancel", finishedAt: Date.now() });
          return;
        }
        const message =
          error instanceof Error ? error.message : t("prediction.failed");
        dispatch({ type: "fail", message, finishedAt: Date.now() });
        toast.error(message);
      } finally {
        if (controllerRef.current === controller) {
          controllerRef.current = null;
        }
      }
    },
    [i18n.language, owner, queryClient, setDraft, t],
  );

  const cancelPrediction = useCallback(() => {
    if (!owner.isCurrent()) return;
    streamRef.current = null;
    userSelectionRef.current = true;
    controllerRef.current?.abort();
    controllerRef.current = null;
    setActiveHistoryId(null);
    dispatch({ type: "cancel", finishedAt: Date.now() });
  }, [owner]);

  const resetRun = useCallback(() => {
    if (!owner.isCurrent()) return;
    streamRef.current = null;
    userSelectionRef.current = true;
    controllerRef.current?.abort();
    controllerRef.current = null;
    setActiveHistoryId(null);
    dispatch({ type: "reset" });
  }, [owner]);

  const showHistoryRecord = useCallback((record: PredictionHistoryRecord) => {
    if (!owner.isCurrent()) return;
    streamRef.current = null;
    userSelectionRef.current = true;
    setLatestRecord(record);
    setDraftState(record.request);
    const status = record.status || "completed";
    if (status === "completed" && record.payload) {
      setActiveHistoryId(null);
      dispatch({
        type: "show",
        request: record.request,
        review: record.review || null,
        payload: record.payload,
      });
      return;
    }
    dispatch({
      type: "restore",
      status: status === "failed" ? "failed" : "running",
      request: record.request,
      review: record.review || null,
      startedAt: record.createdAt,
      currentStep: record.currentStep || null,
      error: record.errorMessage || null,
    });
    setActiveHistoryId(status === "running" ? record.id : null);
  }, [owner]);

  const value = useMemo<PredictionContextValue>(
    () => ({
      run,
      draft,
      latestRecord,
      startPrediction,
      cancelPrediction,
      resetRun,
      setDraft,
      showHistoryRecord,
    }),
    [
      run,
      draft,
      latestRecord,
      startPrediction,
      cancelPrediction,
      resetRun,
      setDraft,
      showHistoryRecord,
    ],
  );

  return (
    <PredictionContext.Provider value={value}>
      {children}
    </PredictionContext.Provider>
  );
}
